import { useCallback, useRef, useState } from 'react';

import type { TFetchError } from '@/api/api-error.types';
import {
  type RoleRequestCreatedResponse,
  type RoleRequestInput,
  RoleRequestStatus,
} from '@/api/role-requests/types';
import { useToast } from '@/shared/design-system';
import { getApiErrorMessage } from '@/utils/api-error-message';

export type RoleChangeBatchResult = {
  applied: number;
  pending: number;
  failed: RoleRequestInput[];
};

const pluralChanges = (count: number) => `${count} change${count === 1 ? '' : 's'}`;
const pluralRequests = (count: number) => `${count} request${count === 1 ? '' : 's'}`;

const sentence = (text: string) => (/[.!?]$/.test(text) ? text : `${text}.`);

/** One message for the whole batch, so a partial failure never shows as two toasts. */
export const batchOutcomeTitle = (
  { applied, pending, failed }: RoleChangeBatchResult,
  failureReason = '',
): string => {
  const done = [
    applied ? `${pluralChanges(applied)} applied` : null,
    pending ? `${pluralRequests(pending)} sent for approval` : null,
  ].filter(Boolean);
  const notSubmitted = failed.length
    ? `${pluralChanges(failed.length)} not submitted: ${failureReason}`
    : null;

  return sentence([done.join(', '), notSubmitted].filter(Boolean).join('; '));
};

/**
 * Submits several role-change requests and reports them as one outcome. Requests go one at a
 * time: the backend allows a single pending request per subject and settles auto-approved ones
 * inside the call, so parallel writes to the same role would race each other. `onFinished` runs
 * once after the last request, so the caches refresh once rather than after every change.
 */
export const useRoleChangeBatch = (
  submit: (_input: RoleRequestInput) => Promise<RoleRequestCreatedResponse>,
  onFinished?: () => void,
) => {
  const { add } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // State lags a render behind, so a second confirm in the same tick would start a second run
  // and send every request twice.
  const inFlight = useRef(false);

  const run = useCallback(
    async (inputs: readonly RoleRequestInput[]): Promise<RoleChangeBatchResult | null> => {
      if (inFlight.current) return null;

      inFlight.current = true;
      setIsSubmitting(true);
      const result: RoleChangeBatchResult = { applied: 0, pending: 0, failed: [] };
      let firstError: unknown = null;

      for (const input of inputs) {
        try {
          // Sequential on purpose — see the hook's comment.
          // eslint-disable-next-line no-await-in-loop
          const created = await submit(input);
          if (created.status === RoleRequestStatus.Approved) {
            result.applied += 1;
          } else {
            result.pending += 1;
          }
        } catch (error) {
          result.failed.push(input);
          firstError ??= error;
        }
      }

      inFlight.current = false;
      setIsSubmitting(false);
      onFinished?.();

      if (inputs.length) {
        const reason = getApiErrorMessage(
          firstError as TFetchError,
          'Could not submit this role change.',
        );
        add({
          title: batchOutcomeTitle(result, reason),
          data: { status: result.failed.length ? 'error' : 'success' },
        });
      }

      return result;
    },
    [add, submit, onFinished],
  );

  return { run, isSubmitting };
};
